import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setPendingImageSearchFile } from '@/lib/pendingImageSearch';

export default function SearchBrowseSection() {
  const { t, i18n } = useTranslation('home');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'upload' | 'camera' | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        const imageUrl = URL.createObjectURL(file);
        setPreviewImage(imageUrl);
        setPreviewMode('upload');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);
      setPreviewMode(fileInputRef.current === e.target ? 'upload' : 'camera');
      setShowUploadOptions(false);
    }
  };

  const handleCameraClick = () => {
    console.log('Camera clicked');
  };

  const handleSearchWithImage = () => {
    if (!selectedFile) return;

    // Pass the file through module-level variable (SPA context stays alive)
    setPendingImageSearchFile(selectedFile);

    setShowUploadOptions(false);
    setPreviewImage(null);
    setPreviewMode(null);
    setSelectedFile(null);

    // Navigate to /packaging where ProductsPage will pick up the file and run its proven image search flow
    navigate('/packaging');
  };

  const handleRetake = () => {
    setPreviewImage(null);
    setPreviewMode(null);
    setSelectedFile(null);
    if (previewMode === 'upload') {
      fileInputRef.current?.click();
    } else if (previewMode === 'camera') {
      cameraInputRef.current?.click();
    } else {
      setShowUploadOptions(true);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/packaging?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/packaging');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      {/* Banner above Quick Search */}
      <section className="w-full bg-white px-4 md:px-6 lg:px-10 pt-6 md:pt-8 lg:pt-10 pb-4 md:pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl px-6 py-8 md:px-10 md:py-12 flex flex-col items-center text-center">
            <h3 className="text-xl md:text-3xl lg:text-4xl font-extrabold text-ink-800 mb-4 md:mb-5 leading-tight tracking-tight">
              {t('search.bannerTitle') || 'Looking for Cosmetic Packaging That Fits Your Brand?'}
            </h3>
            <p className="text-base md:text-lg lg:text-xl text-ink-500 max-w-4xl leading-relaxed mb-6 md:mb-8">
              {t('search.bannerDesc') || "Discover 20,000+ cosmetic packaging options in diverse materials and designs, and get custom finish quotes through GBCOSPACK's one-stop B2B sourcing service."}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-white px-4 md:px-6 lg:px-10 pt-2 md:pt-3 lg:pt-4 pb-2 md:pb-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-ink-800 mb-5 flex items-center justify-center gap-2">
            <i className="ri-flashlight-line text-warm-500 text-2xl" />
            {t('search.quickTitle') || 'Quick Search'}
          </h2>
          <div className="bg-white rounded-lg border border-ink-100 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              {/* Visual Search Button */}
              <button
                onClick={() => setShowUploadOptions(true)}
                className="hidden sm:inline-flex items-center justify-center gap-2.5 px-4 h-11 border-2 border-dashed border-warm-400 hover:border-warm-600 hover:bg-warm-100 text-warm-600 hover:text-warm-700 rounded-lg text-sm font-medium transition-all whitespace-nowrap group"
              >
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-warm-500 text-white text-[10px] font-bold uppercase tracking-wider">
                  AI
                </span>
                <i className="ri-camera-lens-line text-base group-hover:scale-110 transition-transform" />
                <span>{t('search.visual') || 'Upload & Find Similar'}</span>
              </button>

              {/* Divider */}
              <div className="hidden sm:block w-px h-11 bg-ink-300" />

              {/* Search Input */}
              <div className="flex-1 relative ml-6">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 text-lg" />
                <input
                  type="text"
                  placeholder={t('search.placeholder') || 'Search by product name, material, or keyword...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full h-11 pl-10 pr-4 text-sm text-ink-700 placeholder:text-ink-300 bg-cream-50 border border-ink-200 rounded-md outline-none focus:border-warm-500 focus:ring-1 focus:ring-warm-500/20 transition-all"
                />
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="inline-flex items-center justify-center gap-2 px-6 h-11 bg-ink-700 hover:bg-ink-800 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
              >
                <i className="ri-search-line" />
                <span>{t('search.button') || 'Search'}</span>
              </button>
            </div>

            {/* Camera button - mobile only */}
            <button
              onClick={() => setShowUploadOptions(true)}
              className="sm:hidden mt-3 w-full inline-flex items-center justify-center gap-2.5 px-4 py-2.5 border-2 border-dashed border-warm-400 text-warm-600 text-sm rounded-lg hover:bg-warm-100 hover:border-warm-600 transition-colors"
            >
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-warm-500 text-white text-[10px] font-bold uppercase tracking-wider">
                AI
              </span>
              <i className="ri-camera-lens-line" />
              {t('search.visual') || 'Upload & Find Similar'}
            </button>


          </div>

          {/* Browse All Products CTA */}
          <div className="mt-4 flex justify-center">
            <a
              href="/products"
              className="text-sm md:text-base text-ink-600 hover:text-ink-800 underline underline-offset-4 transition-colors"
            >
              {t('search.browseAll') || 'Browse all products'}
            </a>
          </div>
        </div>
      </section>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Upload Options Modal */}
      {showUploadOptions && (
        <div
          className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowUploadOptions(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-8 pt-8 pb-4 text-center">
              <h3 className="text-lg md:text-xl font-bold text-ink-800 mb-2">
                {t('search.searchByImageTitle') || 'Search by Image'}
              </h3>
              <p className="text-sm text-ink-500 max-w-sm mx-auto">
                {t('search.searchByImageDesc') ||
                  'Upload a reference image to find similar packaging.'}
              </p>
            </div>

            {/* Top image area */}
            <div className="bg-ink-50 flex items-center justify-center py-14 md:py-16">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-sm border border-ink-100 flex items-center justify-center">
                <i className="ri-image-line text-4xl text-ink-300" />
              </div>
            </div>

            {/* Bottom buttons */}
            <div className="px-8 py-8">
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 h-12 rounded-lg border border-ink-200 hover:border-warm-500 hover:bg-warm-50 text-ink-700 hover:text-warm-700 text-sm font-medium transition-all"
                >
                  <i className="ri-upload-line" />
                  <span>{t('search.upload') || 'Upload'}</span>
                </button>

                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 h-12 rounded-lg border border-ink-200 hover:border-ink-400 hover:bg-ink-50 text-ink-700 text-sm font-medium transition-all"
                >
                  <i className="ri-camera-line" />
                  <span>{t('search.camera') || 'Camera'}</span>
                </button>

                <button
                  onClick={() => setShowUploadOptions(false)}
                  className="flex items-center justify-center gap-2 h-12 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 text-sm font-medium transition-all"
                >
                  <i className="ri-close-line" />
                  <span>{t('search.cancel') || 'Cancel'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            setPreviewImage(null);
            setPreviewMode(null);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top image area */}
            <div className="bg-ink-50 flex items-center justify-center p-6">
              <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden shadow-sm border border-ink-100 bg-white">
                <img
                  src={previewImage}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Bottom buttons */}
            <div className="px-8 py-8">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleRetake}
                  className="flex items-center justify-center gap-2 h-12 rounded-lg border border-ink-200 hover:border-ink-300 hover:bg-ink-50 text-ink-700 text-sm font-medium transition-all"
                >
                  <i className={previewMode === 'camera' ? 'ri-camera-line' : 'ri-restart-line'} />
                  <span>{previewMode === 'camera' ? (t('search.retake') || 'Retake') : (t('search.reupload') || 'Re-upload')}</span>
                </button>

                <button
                  onClick={handleSearchWithImage}
                  className="flex items-center justify-center gap-2 h-12 rounded-lg bg-ink-700 hover:bg-ink-800 text-white text-sm font-semibold transition-colors"
                >
                  <i className="ri-search-line" />
                  <span>{t('search.findSimilar') || 'Find Similar'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDragging && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center"
        >
          <div className="text-center">
            <i className="ri-upload-cloud-2-line text-5xl text-warm-500 mb-4" />
            <p className="text-white text-lg font-medium">{t('search.dropHere') || 'Drop image to search'}</p>
          </div>
        </div>
      )}
    </>
  );
}